import React, {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {RadioButtonOption} from '../../elements/RadioButtonOption';
import {SelectWithSearch} from '../../selects/SelectWithSearch';
import {isOk} from '../../../utils/apiResponse';
import {UI_VERSION_V2, useUiVersion} from '../../../context/UserModulesContext';
import {adgroupLevelLabel} from '../../../utils/channelLevelLabels';
import {platformName} from '../../agency/connections_v2/platformsText';
import {fetchAccountRule} from '../../ga4_attribution/ga4AttributionApi';
import {
    AGENCY_CARD_URL, isAutomatic, isDirty, isKnownChannel, ruleFromPayload, withField,
} from '../../ga4_attribution/attributionModel';
import {RAW, ruleSummaryText} from '../../ga4_attribution/attributionText';
import {AttributionRuleFields} from '../../ga4_attribution/AttributionRuleFields';
import {Ga4AttributionCoverage} from '../../ga4_attribution/Ga4AttributionCoverage';
import {useGa4AttributionPreview} from '../../ga4_attribution/useGa4AttributionPreview';
import '../../../sass/elements/_ga4-attribution.scss';

export const MODE_AGENCY = 'agency';
export const MODE_CUSTOM = 'custom';

/** El GET de la cuenta, en lo que la pantalla usa (`contrato.md` § 3). */
function accountRuleFromPayload(body) {
    const data = body || {};
    return {
        supported: data.supported !== false,
        channel: data.channel,
        canEdit: data.can_edit !== false,
        mode: data.mode === MODE_CUSTOM && data.custom_rule ? MODE_CUSTOM : MODE_AGENCY,
        agencyRuleRaw: data.agency_rule || null,
        agencyRule: ruleFromPayload(data.agency_rule),
        customRule: data.custom_rule ? ruleFromPayload(data.custom_rule) : null,
        properties: Array.isArray(data.ga4_properties) ? data.ga4_properties : [],
        defaultPropertyId: data.default_ga4_property_id ? String(data.default_ga4_property_id) : null,
    };
}

/** La propiedad con la que arranca la vista previa: la que usan sus líneas, o la única que hay. */
function initialPropertyId(account) {
    if (account.defaultPropertyId) return account.defaultPropertyId;
    const usable = account.properties.filter((property) => property.has_access !== false);
    return usable.length === 1 ? String(usable[0].account_id) : null;
}

/**
 * «Cruce con GA4» en el modal de la cuenta del cliente: si esta cuenta usa la regla de la agencia para
 * su plataforma o una propia, y qué cruzaría cada una con los últimos 30 días de GA4.
 *
 * Pide la regla al montarse, que es al abrir el modal (reactstrap monta el contenido recién ahí). No
 * guarda nada: avisa el borrador con `onDraftChange(dirty ? body : null)` y el formulario del modal hace
 * el PUT antes del POST del límite diario. Un 404 (backend viejo, o cuenta que no es de la agencia) no
 * dibuja nada.
 *
 * Vive dentro del `<form>` del modal: todo botón propio va con `type="button"`, o un clic lo enviaría, y
 * un Enter en el buscador de la propiedad no puede enviarlo (guardaría la regla que se está probando).
 *
 * `disabled`: mientras el modal guarda, nada se puede cambiar (lo que se tocara no viajaría en el PUT).
 */
export function Ga4AttributionSection({clientAdAccountId, channel, onDraftChange, seeRuleLink = true, disabled = false}) {
    const {t} = useTranslation();
    const {setUiVersion} = useUiVersion();
    const titleId = useId();
    const propertyLabelId = useId();
    const [status, setStatus] = useState('loading');
    const [account, setAccount] = useState(null);
    const [mode, setMode] = useState(MODE_AGENCY);
    const [customRule, setCustomRule] = useState(null);
    const [propertyId, setPropertyId] = useState(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        fetchAccountRule(clientAdAccountId).then((response) => {
            if (cancelled) return;
            if (response && response.absent) {
                setStatus('absent');
                return;
            }
            if (!isOk(response) || !response.data) {
                setStatus('error');
                return;
            }
            const next = accountRuleFromPayload(response.data);
            setAccount(next);
            setMode(next.mode);
            setCustomRule(next.customRule);
            setPropertyId(initialPropertyId(next));
            setStatus(next.supported ? 'ready' : 'absent');
        });
        return () => { cancelled = true; };
    }, [clientAdAccountId, attempt]);

    const effectiveChannel = (account && account.channel) || channel;
    const automatic = isAutomatic(effectiveChannel, account && account.agencyRuleRaw);
    const draftCustom = customRule || (account ? account.agencyRule : null);

    const dirty = Boolean(account) && !automatic && account.canEdit && (
        mode !== account.mode
        || (mode === MODE_CUSTOM && isDirty(account.customRule || account.agencyRule, draftCustom))
    );
    const draftBody = mode === MODE_CUSTOM ? {mode: MODE_CUSTOM, rule: draftCustom} : {mode: MODE_AGENCY};
    const draftSignature = dirty ? JSON.stringify(draftBody) : '';

    // El padre arma el callback en cada render: se lee de un ref para que el aviso dependa sólo del
    // borrador, no de su identidad.
    const onDraftChangeRef = useRef(onDraftChange);
    onDraftChangeRef.current = onDraftChange;
    useEffect(() => {
        if (onDraftChangeRef.current) onDraftChangeRef.current(draftSignature ? JSON.parse(draftSignature) : null);
    }, [draftSignature]);

    const ruleToPreview = automatic || !account ? null : (mode === MODE_CUSTOM ? draftCustom : account.agencyRule);
    const preview = useGa4AttributionPreview(
        status === 'ready' ? clientAdAccountId : null, ruleToPreview, propertyId,
    );

    const setField = useCallback((field, value) => {
        setCustomRule((current) => withField(current || (account && account.agencyRule), field, value));
    }, [account]);

    const propertyOptions = useMemo(() => (account ? account.properties.map((property) => ({
        id: String(property.account_id), text: property.name,
    })) : []), [account]);
    const noAccessIds = useMemo(() => (account ? account.properties
        .filter((property) => property.has_access === false)
        .map((property) => String(property.account_id)) : []), [account]);

    if (status === 'absent' || (!isKnownChannel(effectiveChannel) && status !== 'loading')) return null;

    const platform = platformName(effectiveChannel);
    const levelPlural = adgroupLevelLabel(t, effectiveChannel, {count: 2});

    let body;
    if (status === 'loading') {
        body = <div className="ga4-attr-section__loader"><Spinner color="positive small"/></div>;
    } else if (status === 'error') {
        body = (
            <p className="ga4-attr-section__error" role="status">
                {t('ga4_attribution.agency.load_error')}{' '}
                <button type="button" className="ga4-attr-coverage__retry" onClick={() => setAttempt((n) => n + 1)}>
                    {t('ga4_attribution.agency.load_error_retry')}
                </button>
            </p>
        );
    } else {
        body = (
            <>
                <fieldset className="ga4-attr-section__rule">
                    <legend className="ga4-attr-section__legend">
                        {t('ga4_attribution.account.legend', {levelPlural, interpolation: RAW})}
                    </legend>
                    {automatic && <p className="ga4-attr-section__auto">{t('ga4_attribution.summary.google_auto')}</p>}
                    {!automatic && !account.canEdit && (
                        <ReadOnlyRule t={t} account={account} channel={effectiveChannel} platform={platform}/>
                    )}
                    {!automatic && account.canEdit && (
                        <>
                            <RadioButtonOption
                                isSelected={mode === MODE_AGENCY}
                                onClick={() => { if (!disabled) setMode(MODE_AGENCY); }}
                                label={(
                                    <span className="ga4-attr-option">
                                        <strong className="ga4-attr-option__title">
                                            {t('ga4_attribution.account.use_agency', {platform, interpolation: RAW})}
                                        </strong>
                                        <span className="ga4-attr-option__summary">
                                            {ruleSummaryText(t, effectiveChannel, account.agencyRule)}
                                            {seeRuleLink && (
                                                <>
                                                    {' '}
                                                    {/* La tarjeta existe sólo en la 2.1: desde el clásico
                                                        el link cambia de marco, como la nota del clásico. */}
                                                    <Link className="ga4-attr-option__link" to={AGENCY_CARD_URL}
                                                          onClick={() => setUiVersion(UI_VERSION_V2)}>
                                                        {t('ga4_attribution.account.see_rule')}
                                                    </Link>
                                                </>
                                            )}
                                        </span>
                                    </span>
                                )}/>
                            <RadioButtonOption
                                isSelected={mode === MODE_CUSTOM}
                                onClick={() => { if (!disabled) setMode(MODE_CUSTOM); }}
                                label={<strong className="ga4-attr-option__title">{t('ga4_attribution.account.customize')}</strong>}
                                note={t('ga4_attribution.account.customize_note')}>
                                <AttributionRuleFields channel={effectiveChannel} rule={draftCustom}
                                                       onChange={setField} layout="stacked" disabled={disabled}/>
                            </RadioButtonOption>
                        </>
                    )}
                </fieldset>
                <div className="ga4-attr-section__property">
                    <span className="ga4-attr-section__label" id={propertyLabelId}>
                        {t('ga4_attribution.account.property_label')}
                    </span>
                    {propertyOptions.length > 0 ? (
                        // Enter en el buscador enviaría el `<form>` del modal (PUT de la regla en borrador +
                        // POST del límite + cierre). Se corta acá y no en `SelectWithSearch`, que es compartido.
                        <div role="group" aria-labelledby={propertyLabelId}
                             onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }}>
                            {/* `SelectWithSearch` avisa también el clic en una opción no seleccionable
                                (sólo no la marca): una propiedad sin acceso se ignora acá. */}
                            <SelectWithSearch options={propertyOptions} selected={propertyId}
                                              optionosNotSelectable={noAccessIds}
                                              onSelectOptionChange={(option) => {
                                                  const id = option && option.id ? String(option.id) : null;
                                                  if (id && noAccessIds.includes(id)) return;
                                                  setPropertyId(id);
                                              }}/>
                        </div>
                    ) : (
                        <p className="ga4-attr-section__note">{t('ga4_attribution.account.property_none')}</p>
                    )}
                </div>
                {propertyId && (
                    <Ga4AttributionCoverage channel={effectiveChannel} data={preview.data} loading={preview.loading}
                                            errorCode={preview.errorCode} onRetry={preview.retry}/>
                )}
                <p className="ga4-attr-section__note">{t('ga4_attribution.account.scope_note')}</p>
            </>
        );
    }

    return (
        <section className="ga4-attr-section" aria-labelledby={titleId} data-testid="ga4-attr-section">
            <h3 className="ga4-attr-section__title" id={titleId}>{t('ga4_attribution.account.title')}</h3>
            {body}
        </section>
    );
}

/** Sin permiso para cambiarla: qué regla usa la cuenta y por qué no se puede tocar. */
function ReadOnlyRule({t, account, channel, platform}) {
    const custom = account.mode === MODE_CUSTOM;
    return (
        <div className="ga4-attr-section__readonly">
            <p className="ga4-attr-section__lock">
                <FaIcon iconName="lock"/>
                <span>{t('ga4_attribution.agency.forbidden')}</span>
            </p>
            <p className="ga4-attr-option__title">
                {custom
                    ? t('ga4_attribution.source.account')
                    : t('ga4_attribution.source.agency', {platform, interpolation: RAW})}
            </p>
            <p className="ga4-attr-option__summary">
                {ruleSummaryText(t, channel, custom ? account.customRule : account.agencyRule)}
            </p>
        </div>
    );
}

export default Ga4AttributionSection;
